const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export function isBrandColor(color?: string | null): color is string {
  return !!color && HEX_COLOR.test(color);
}

function hexToOklch(hex: string): string {
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const toLinear = (c: number) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  const x = 0.4124564 * lr + 0.3575761 * lg + 0.1804375 * lb;
  const y = 0.2126729 * lr + 0.7151522 * lg + 0.072175 * lb;
  const z = 0.0193339 * lr + 0.119192 * lg + 0.9503041 * lb;

  const l_ = 0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z;
  const m_ = 0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z;
  const s_ = 0.0482003018 * x + 0.2643662691 * y + 0.6338517028 * z;

  const l = Math.cbrt(l_);
  const m = Math.cbrt(m_);
  const s = Math.cbrt(s_);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bVal = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const C = Math.sqrt(a * a + bVal * bVal);
  let H = (Math.atan2(bVal, a) * 180) / Math.PI;
  if (H < 0) H += 360;

  return `oklch(${L.toFixed(4)} ${C.toFixed(4)} ${H.toFixed(4)})`;
}

function getContrastingForeground(hex: string): string {
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "oklch(0.2050 0 0)" : "oklch(0.9850 0 0)";
}

interface BrandThemeProps {
  brandColor?: string | null;
  children: React.ReactNode;
}

/**
 * Applies a merchant brand color to the theme tokens. Rendered as a <style>
 * element so the color is in the server HTML (no flash of the default color)
 * and leaves with the component on unmount. `html:root` outranks the
 * `:root` defaults in globals.css.
 */
export function BrandTheme({ brandColor, children }: BrandThemeProps) {
  if (!isBrandColor(brandColor)) return <>{children}</>;

  const primary = hexToOklch(brandColor);
  const css = `html:root{--primary:${primary};--primary-foreground:${getContrastingForeground(brandColor)};--ring:${primary}}`;

  return (
    <>
      <style>{css}</style>
      {children}
    </>
  );
}
